import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { authApi, fetchMe, login as apiLogin, setAccessToken, getAccessToken, type Principal } from '../lib/api';

type AuthState = {
  principal: Principal | null;
  loading: boolean;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      setLoading(false);
      return;
    }
    fetchMe()
      .then(setPrincipal)
      .catch(() => setAccessToken(null))
      .finally(() => setLoading(false));
  }, []);

  const value = useMemo<AuthState>(() => ({
    principal,
    loading,
    async login(email, password) {
      const token = await apiLogin(email, password);
      setAccessToken(token);
      setPrincipal(await fetchMe());
    },
    async logout() {
      // Révoquer la session côté serveur AVANT d'oublier le jeton : sans cet
      // appel, le jeton reste valable jusqu'à son expiration et se déconnecter
      // n'aurait aucun effet réel.
      try {
        await authApi.logout();
      } catch {
        // Serveur injoignable : on efface tout de même localement.
      }
      setAccessToken(null);
      setPrincipal(null);
    },
  }), [principal, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext<AuthState | null>(AuthContext);
  if (!value) throw new Error('useAuth must be used within AuthProvider');
  return value;
}
